import * as React from "react"

import { cn } from "@/lib/utils"
import { Textarea } from "@/components/ui/textarea"

type AutoSizingTextareaProps = Omit<React.ComponentProps<"textarea">, "value" | "onChange" | "rows"> & {
  value: string
  onChange: (value: string) => void
}

const AutoSizingTextarea = React.forwardRef<HTMLTextAreaElement, AutoSizingTextareaProps>(
  ({ value, onChange, className, ...props }, forwardedRef) => {
    const ref = React.useRef<HTMLTextAreaElement>(null)

    React.useImperativeHandle(forwardedRef, () => ref.current as HTMLTextAreaElement)

    React.useLayoutEffect(() => {
      const element = ref.current
      if (!element) return

      const resize = () => {
        element.style.height = "auto"
        element.style.height = `${element.scrollHeight}px`
      }

      resize()

      // Without this a layout change rewraps the text taller while the pixel height stays put.
      const observer = new ResizeObserver(resize)
      observer.observe(element)
      return () => observer.disconnect()
    }, [value])

    return (
      <Textarea
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={1}
        className={cn("min-h-0 resize-none overflow-hidden bg-background text-sm leading-relaxed", className)}
        {...props}
      />
    )
  }
)
AutoSizingTextarea.displayName = "AutoSizingTextarea"

export { AutoSizingTextarea }
